/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * Calendar 클래스를 정의한다.
 *
 * @file /src/elements/calendar/Calendar.ts
 * @author sungjin <esung246@naddle.net>
 * @license MIT License
 * @modified 2026. 7. 7.
 */
import * as parent from '@base/Element';
import { FormatDate } from '@utils/FormatDate';
import { Dom } from '@utils/Dom';
import { Html } from '@utils/Html';
import { DomList } from '@utils/DomList';
import { Language } from '@utils/Language';
import './Calendar.css';

export interface Listeners<T> extends parent.Listeners<T> {
    /**
     * 캘린더 날짜를 클릭하였을때
     */
    click?: (date: FormatDate, $date: Dom, me: T) => void | Promise<void>;

    /**
     * 캘린더 날짜에 마우스 오버하였을때
     */
    hover?: (date: FormatDate, $date: Dom, me: T) => void | Promise<void>;

    /**
     * 포커스 날짜가 변경되었을 때
     */
    change?: (me: T, current: FormatDate) => void | Promise<void>;
}

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 헤더를 보여줄지 여부
     */
    header?: boolean;

    /**
     * 푸터를 보여줄지 여부
     */
    footer?: boolean;

    /**
     * 현재날짜
     */
    current?: FormatDate;

    /**
     * 날짜 범위 선택 옵션
     */
    range?: boolean;

    /**
     * 설정된 시작일
     */
    start?: FormatDate;

    /**
     * 설정된 종료일
     */
    end?: FormatDate;

    /**
     * 이벤트리스너
     */
    listeners?: Listeners<T>;
}

export class Calendar extends parent.Element {
    /**
     * 엘리먼트 타입
     */
    type = 'calendar';

    /**
     * 헤더를 보여줄지 여부
     */
    header: boolean;

    /**
     * 푸터를 보여줄지 여부
     */
    footer: boolean;

    /**
     * 현재날짜
     */
    current: FormatDate;

    /**
     * 설정된 시작일
     */
    start: FormatDate | null = null;

    /**
     * 설정된 종료일
     */
    end: FormatDate | null = null;

    /**
     * 달 이동 시 사용하는 타임아웃
     */
    spinTimeout: ReturnType<typeof setTimeout>;

    /**
     * 헤더 DOM
     */
    $header: Dom = null;

    /**
     * 콘텐츠 DOM
     */
    $content: Dom = null;

    /**
     * 날짜선택기 DOM
     */
    $dates: Dom = null;

    /**
     * 푸터 DOM
     */
    $footer: Dom = null;

    /**
     * 현재 월 버튼 DOM
     */
    $month: Dom = null;

    /**
     * 피커 DOM
     */
    $picker: Dom = null;

    /**
     * 마지막에 선택 날짜
     */
    selectedDate: FormatDate = null;

    /**
     * 캘린더 엘리먼트 클래스를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Properties<Calendar> = null) {
        super(properties);

        this.header = this.properties.header === true;
        this.footer = this.properties.footer === true;
        this.current = this.properties.current ?? new FormatDate();
        this.start = this.properties.start ?? null;
        this.end = this.properties.end ?? null;
    }

    /**
     * 헤더 DOM 을 가져온다.
     *
     * @returns 헤더 DOM
     */
    $getHeader(): Dom {
        if (this.$header === null) {
            this.$header = Html.create('div', { 'data-role': 'header' });

            this.$header.append(this.$getMonth());

            const $prev = Html.create('button', { type: 'button', 'data-action': 'prev' });
            $prev.on('pointerdown', (_, e: PointerEvent) => {
                this.startSpin('prev');
                e.stopPropagation();
            });
            $prev.on('pointerup', (_, e: PointerEvent) => {
                this.stopSpin();
                e.stopPropagation();
            });
            $prev.on('pointerout', (_, e: PointerEvent) => {
                this.stopSpin();
                e.stopPropagation();
            });
            this.$header.append($prev);

            const $next = Html.create('button', { type: 'button', 'data-action': 'next' });
            $next.on('pointerdown', (_, e: PointerEvent) => {
                this.startSpin('next');
                e.stopPropagation();
            });
            $next.on('pointerup', (_, e: PointerEvent) => {
                this.stopSpin();
                e.stopPropagation();
            });
            $next.on('pointerout', (_, e: PointerEvent) => {
                this.stopSpin();
                e.stopPropagation();
            });
            this.$header.append($next);

            this.$header.append(this.$getPicker());
        }

        return this.$header;
    }

    /**
     * 콘텐츠 DOM 을 가져온다.
     *
     * @returns 콘텐츠 DOM
     */
    $getContent(): Dom {
        if (this.$content === null) {
            this.$content = Html.create('div', { 'data-role': 'content' });

            const startDate = new FormatDate().getStartDateOfWeek();
            const $days = Html.create('ul', { 'data-role': 'days' });
            for (let i = 0; i < 7; i++) {
                const date = startDate.clone().addDate(i);
                const $day = Html.create('li', { 'data-day': String(date.format('w')) }, date.format('D'));
                $days.append($day);
            }
            this.$content.append($days);

            this.$content.append(this.$getDates());
        }

        return this.$content;
    }

    /**
     * 날짜선택기 DOM 을 가져온다.
     *
     * @returns 날짜선택기 DOM
     */
    $getDates(): Dom {
        if (this.$dates === null) {
            this.$dates = Html.create('ul', { 'data-role': 'dates' }, '<li></li>'.repeat(42));

            this.$dates.on('click', async (_, e) => {
                const $date = Html.el(e.target).getParents('li[data-value]');
                if ($date !== null && !$date.hasClass('disabled')) {
                    this.selectedDate = $date.getData('date');
                    await this.fireEvent('click', [$date.getData('date'), $date, this]);
                }
            });

            this.$dates.on('pointermove', async ($dates, e) => {
                const $date = Html.el(e.target).getParents('li[data-value]');

                if (
                    $date !== null &&
                    !$date.hasClass('disabled') &&
                    $dates.getData('hover') !== $date.getData('date')
                ) {
                    $dates.setData('hover', $date.getData('date'));
                    await this.fireEvent('hover', [$date.getData('date'), $date, this]);
                }
            });
        }

        return this.$dates;
    }

    /**
     * 푸터 DOM 을 가져온다.
     *
     * @returns 푸터 DOM
     */
    $getFooter(): Dom {
        if (this.$footer === null) {
            this.$footer = Html.create('div', { 'data-role': 'footer' });

            const $today = Html.create('button', { type: 'button' }, Language.get('buttons.today'));
            $today.on('click', () => {
                this.setCurrent(new FormatDate());
            });
            this.$footer.append($today);
        }

        return this.$footer;
    }

    /**
     * 현재 월 버튼 DOM 을 가져온다.
     *
     * @returns 현재 월 버튼 DOM
     */
    $getMonth(): Dom {
        if (this.$month === null) {
            this.$month = Html.create('button', { type: 'button', 'data-action': 'month' }, this.current.format('Y.m'));

            this.$month.on('click', () => {
                this.$getHeader().toggleClass('picker');

                Html.get('li[data-year="' + this.current.getYear() + '"]', this.$getPicker()).trigger('click');
                Html.get('li[data-month="' + this.current.getMonth() + '"]', this.$getPicker()).trigger('click');
            });
        }

        return this.$month;
    }

    /**
     * 피커 DOM 을 가져온다.
     *
     * @returns 피커 DOM
     */
    $getPicker(): Dom {
        if (this.$picker == null) {
            this.$picker = Html.create('div', { 'data-role': 'picker' });

            const formatted = new FormatDate();
            const today = new FormatDate();
            const $years = Html.create('ul', { 'data-role': 'years' });
            for (let i = today.getYear() + 5; i >= 1900; i--) {
                const year = formatted.setYear(i);
                const $year = Html.create('li', null, year.format('E'));
                $year.setAttr('data-year', year.getYear().toString());

                $year.on('click', ($me) => {
                    const $selected = Html.get('li[data-year].selected', $years);
                    $selected?.removeClass('selected');
                    $me.addClass('selected');
                    $me.scrollTo(true, $years);
                });

                $years.append($year);
            }
            this.$picker.append($years);

            const $months = Html.create('ul', { 'data-role': 'months' });

            for (let i = 1; i <= 12; i++) {
                const month = formatted.setMonth(i);
                const $month = Html.create('li', null, month.format('F'));
                $month.setAttr('data-month', month.getMonth().toString());

                $month.on('click', ($me) => {
                    const $selected = Html.get('li[data-month].selected', $months);
                    $selected?.removeClass('selected');
                    $me.addClass('selected');
                });

                $months.append($month);
            }
            this.$picker.append($months);

            const $confirm = Html.create('div');
            const $button = Html.create('button', { type: 'button' }, Language.get('buttons.ok'));
            $button.on('click', async () => {
                const year = parseInt(
                    Html.get('li.selected', $years)?.getAttr('data-year') ?? this.current.getYear().toString(),
                    10
                );
                const month = parseInt(
                    Html.get('li.selected', $months)?.getAttr('data-month') ?? this.current.getMonth().toString(),
                    10
                );

                await this.setCurrent(this.current.clone().setYear(year).setMonth(month), false);
                this.$getHeader().removeClass('picker');
            });
            $confirm.append($button);

            this.$picker.append($confirm);
        }

        return this.$picker;
    }

    /**
     * 특정날짜가 현재 캘린더에 표시되고 있다면, 해당 날짜의 DOM 을 가져온다.
     *
     * @param date - 날짜
     * @returns 해당날짜의 DOM
     */
    $getDate(date: FormatDate): Dom | null {
        return Html.get('li[data-value="' + date.format('Y-m-d') + '"]', this.$getDates());
    }

    /**
     * 특정날짜범위가 현재 캘린더에 표시되고 있다면, 해당 날짜의 DOM 을 가져온다.
     *
     * @param start - 범위시작일 (NULL 일 경우 종료일 모두)
     * @param end - 범위종료일 (NULL 일 경우 시작일 이후 모두)
     * @returns 해당날짜의 DOMList
     */
    $getRange(start: FormatDate = null, end: FormatDate = null): DomList {
        const dates: HTMLElement[] = [];
        this.$getDates()
            .getEl()
            .querySelectorAll('li[data-value]')
            .forEach((node: HTMLElement) => {
                if (start === null && end === null) {
                    dates.push(node);
                } else if (start !== null && end !== null) {
                    if (
                        node.getAttribute('data-value') >= start.format('Y-m-d') &&
                        node.getAttribute('data-value') <= end.format('Y-m-d')
                    ) {
                        dates.push(node);
                    } else if (
                        node.getAttribute('data-value') <= start.format('Y-m-d') &&
                        node.getAttribute('data-value') >= end.format('Y-m-d')
                    ) {
                        dates.push(node);
                    }
                } else if (start !== null) {
                    if (node.getAttribute('data-value') >= start.format('Y-m-d')) {
                        dates.push(node);
                    }
                } else if (end !== null) {
                    if (node.getAttribute('data-value') <= end.format('Y-m-d')) {
                        dates.push(node);
                    }
                }
            });

        return new DomList(dates);
    }

    /**
     * 마지막에 선택한 날짜를 가져온다.
     *
     * @returns 마지막 선택 날짜
     */
    getSelectedDate(current: FormatDate, is_fire_event: boolean = true): FormatDate | null {
        return this.selectedDate;
    }

    /**
     * 현재 날짜를 변경한다.
     *
     * @param current - 포커스할 날짜 (년월만 활용한다.)
     * @param is_fire_event - 이벤트 발생여부
     */
    async setCurrent(current: FormatDate, is_fire_event: boolean = true): Promise<void> {
        current ??= new FormatDate();
        let updateMonth: boolean = this.current.format('Y-m') != current.format('Y-m');
        this.current = current;

        if (updateMonth == true) {
            if (this.header == true) {
                this.$getMonth().html(this.current.format('Y.m'));
            }

            this.renderCalendar();
        }

        if (is_fire_event == true) {
            await this.fireEvent('change', [this, this.current]);
        }
    }

    /**
     * 달을 이동한다.
     *
     * @param direction - 이동할 방향
     * @param is_interval - 지속이동여부
     */
    async startSpin(direction: string, is_interval: boolean = false): Promise<void> {
        this.stopSpin();
        await this.setCurrent(this.current.clone().addMonth(direction == 'prev' ? -1 : 1));

        this.spinTimeout = setTimeout(this.startSpin.bind(this), is_interval == true ? 100 : 500, direction, true);
    }

    /**
     * 달 이동을 중단한다.
     */
    stopSpin(): void {
        if (this.spinTimeout) {
            clearTimeout(this.spinTimeout);
            this.spinTimeout = null;
        }
    }

    /**
     * 캘린더를 랜더링한다.
     */
    renderCalendar(): void {
        const firstDate = this.current.getFirstDateOfMonth();
        const startDate = firstDate.getStartDateOfWeek();
        const today = new FormatDate();

        Html.all('li', this.$getDates()).forEach(($date, index) => {
            const date = startDate.clone().addDate(index);
            $date.setData('date', date);
            $date.setAttr('data-value', date.format('Y-m-d'));
            $date.removeAttr('class');

            if (date.getMonth() != firstDate.getMonth()) {
                $date.addClass('unfocused');
            }

            if (today.format('Y-m-d') == date.format('Y-m-d')) {
                $date.addClass('today');
            }

            if (date.getDay() == 0) {
                $date.addClass('sun');
            }

            if (date.getDay() == 6) {
                $date.addClass('sat');
            }

            const dateStr = date.format('Y-m-d');
            const isBeforeStart = this.start !== null && dateStr < this.start.format('Y-m-d');
            const isAfterEnd = this.end !== null && dateStr > this.end.format('Y-m-d');
            if (isBeforeStart || isAfterEnd) {
                $date.addClass('disabled');
            }

            $date.html(date.getDate().toString());
        });
    }

    /**
     * 캘린더를 랜더링한다.
     */
    async render(): Promise<void> {
        if (this.isRendered() == true) {
            return;
        }

        if (this.isHidden() == true) {
            this.hide();
        }

        if (this.header == true) {
            this.$getElement().append(this.$getHeader());
        }

        this.$getElement().append(this.$getContent());
        this.renderCalendar();

        if (this.footer == true) {
            this.$getElement().append(this.$getFooter());
        }

        await this.onRender();
        await this.fireEvent('change', [this, this.current]);
    }
}
